import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import WorkOrderBomLinkText from '#/modules/momMgmt/workOrder/components/WorkOrderBomLinkText';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';
import {
  displayOrDash,
  mapWorkOrderMeta,
} from '#/modules/momMgmt/workOrder/helper/mapWorkOrderMeta';

function WorkOrderHero({ meta, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{meta.code}</h1>
            {meta.status ? <WorkOrderStatusBadge status={meta.status} /> : null}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            成品：
            {meta.productSubtitle}
          </p>
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function WorkOrderInfoGrid({ meta }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <div>
        <p className="text-xs text-gray-500">建立</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.createDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">修改</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.reviseDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">完成</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.closeDate}</p>
      </div>
      <div className="hidden md:block" aria-hidden="true" />
      <div>
        <p className="text-xs text-gray-500">排程日</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.scheduledStartDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">加工廠</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.processingFactory}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">工作倉</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.workWarehouse}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">目標倉</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.targetWarehouse}</p>
      </div>
      <div className="sm:col-span-2 md:col-span-4">
        <p className="text-xs text-gray-500">BOM</p>
        <WorkOrderBomLinkText
          label={meta.bomLabel}
          bomId={meta.bomId}
          className="mt-0.5 text-sm font-medium"
        />
      </div>
      <div>
        <p className="text-xs text-gray-500">製造量</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.manufacturingQty}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">已入庫總量</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.completedQty}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">報廢量</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.scrapQty}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">取消量</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.cancelQty}</p>
      </div>
      <div className="col-span-1 md:col-span-4">
        <p className="text-xs text-gray-500">有效製造量</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.effectiveManufacturingQty}</p>
      </div>
      <div className="col-span-1 sm:col-span-2">
        <p className="text-xs text-gray-500">事由</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.issueReason)}
        </p>
      </div>
      <div className="col-span-1 sm:col-span-2">
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

WorkOrderHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
    productSubtitle: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
};

WorkOrderInfoGrid.propTypes = {
  meta: PropTypes.object.isRequired,
};

const WorkOrderMainInfoCard = ({
  detail,
  embedded = false,
  actions = null,
}) => {
  const meta = mapWorkOrderMeta(detail);

  const content = (
    <>
      <WorkOrderHero meta={meta} actions={actions} />
      <WorkOrderInfoGrid meta={meta} />
    </>
  );

  if (embedded) {
    return (
      <div className="border-b border-slate-200 p-5">
        {content}
      </div>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        {content}
      </div>
    </div>
  );
};

WorkOrderMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  actions: PropTypes.node,
};

export default WorkOrderMainInfoCard;
